import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import UserAvatar from '../../util/UserAvatar';

interface PendingReport {
  id: number;
  userId: number;
  week: string;
  startDate?: string;
  endDate?: string;
  user?: { id: number; first_name: string; last_name: string; avatarUrl?: string };
}

interface TeamMember {
  id: number;
  first_name: string;
  last_name: string;
  avatarUrl?: string;
  workingStatus?: { id: number; name: string; emoji: string; color: string } | null;
}

interface PendingExpense {
  id: number;
  amount: number;
  userId: number;
  user?: { first_name: string; last_name: string };
}


const SmartSummaryTab: React.FC = () => {
  const auth = useSelector((state: RootState) => state.auth.value);
  const [pendingReports, setPendingReports] = useState<PendingReport[]>([]);
  const [pendingExpenses, setPendingExpenses] = useState<PendingExpense[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [userRes, usersRes] = await Promise.all([
        axios.get('user'),
        axios.get('users'),
      ]);
      const me = userRes.data;
      setTeamMembers(Array.isArray(usersRes.data) ? usersRes.data : []);

      // Pending timesheets for supervisor
      try {
        const { data: reps } = await axios.get(`time-reports/${me.id}`);
        const pending = (Array.isArray(reps) ? reps : []).filter(
          (r: any) => r.status === 'PENDING' || r.status === 'pending'
        );
        setPendingReports(pending);
      } catch {}

      // Pending expenses
      try {
        const endTime = new Date();
        const startTime = new Date(endTime.getFullYear(), endTime.getMonth() - 3, 1);
        const { data: expsData } = await axios.post('expenses', { startTime, endTime });
        const expsList = Array.isArray(expsData?.expenses) ? expsData.expenses : [];
        const pendingExp = expsList.filter((e: any) => e.status === 'pending');
        setPendingExpenses(pendingExp);
      } catch {}
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { if (auth) fetchAll(); }, [auth, fetchAll]);

  if (loading) return (
    <div style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-color-calendar-week)', fontSize: 12 }}>Loading…</div>
  );

  // Deduplicate pending reports by user
  const uniqueUsers = new Map<number, PendingReport>();
  pendingReports.forEach(r => { if (!uniqueUsers.has(r.userId)) uniqueUsers.set(r.userId, r); });
  const pendingApprovals = Array.from(uniqueUsers.values()).slice(0, 5);

  // Team with working status
  const teamWithStatus = teamMembers.filter(m => m.workingStatus).slice(0, 6);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>

      {/* Needs Attention */}
      {(pendingReports.length > 0 || pendingExpenses.length > 0) && (
        <div style={{ background: 'var(--surface2)', border: '1px solid rgba(239,68,68,.2)', borderRadius: 12, padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: '#ef4444' }}>
              Needs attention
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pendingReports.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', borderRadius: 8, background: 'rgba(239,68,68,.05)', border: '1px solid rgba(239,68,68,.12)', cursor: 'pointer' }}>
                <span style={{ fontSize: 15 }}>✅</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-new)' }}>{pendingReports.length} timesheet{pendingReports.length > 1 ? 's' : ''}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>waiting approval</div>
                </div>
                <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 800, background: 'rgba(239,68,68,.15)', color: '#ef4444' }}>{pendingReports.length}</span>
              </div>
            )}
            {pendingExpenses.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 9px', borderRadius: 8, background: 'rgba(217,119,6,.05)', border: '1px solid rgba(217,119,6,.12)', cursor: 'pointer' }}>
                <span style={{ fontSize: 15 }}>💰</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-new)' }}>{pendingExpenses.length} expense{pendingExpenses.length > 1 ? 's' : ''}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>pending review</div>
                </div>
                <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 800, background: 'rgba(217,119,6,.12)', color: '#d97706' }}>{pendingExpenses.length}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pending Approvals */}
      {pendingApprovals.length > 0 && (
        <div style={{ background: 'var(--surface2)', border: '1px solid var(--border2-new)', borderRadius: 12, padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#d97706', flexShrink: 0 }} />
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)' }}>
              Pending Approvals
            </span>
          </div>
          {pendingApprovals.map((r, i) => {
            const name = r.user
              ? `${r.user.first_name} ${r.user.last_name}`
              : `User ${r.userId}`;
            const weekLabel = r.startDate
              ? `W.${new Date(r.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
              : `Week ${i + 1}`;
            return (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < pendingApprovals.length - 1 ? '1px solid var(--border2-new)' : 'none' }}>
                <UserAvatar user={r.user ?? { id: r.userId }} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-new)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>{weekLabel}</div>
                </div>
                <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 9, fontWeight: 700, background: 'rgba(37,99,235,.1)', color: '#2563eb', flexShrink: 0 }}>Time</span>
              </div>
            );
          })}
          {pendingExpenses.slice(0, 3).map((e, i) => {
            const name = e.user ? `${e.user.first_name} ${e.user.last_name}` : `User ${e.userId}`;
            return (
              <div key={`exp-${e.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: '1px solid var(--border2-new)' }}>
                <UserAvatar user={e.user ?? { id: e.userId }} size={28} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-new)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)' }}>£{Number(e.amount || 0).toFixed(2)}</div>
                </div>
                <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 9, fontWeight: 700, background: 'rgba(217,119,6,.1)', color: '#d97706', flexShrink: 0 }}>Exp.</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Team Status */}
      {teamWithStatus.length > 0 && (
        <div style={{ background: 'var(--surface2)', border: '1px solid var(--border2-new)', borderRadius: 12, padding: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#059669', flexShrink: 0 }} />
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)' }}>
              Team Status
            </span>
          </div>
          {teamWithStatus.map((m, i) => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: i < teamWithStatus.length - 1 ? '1px solid var(--border2-new)' : 'none' }}>
              <span style={{
                width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                background: m.workingStatus?.color || '#6b7280',
              }} />
              <div style={{ flex: 1, fontSize: 11, fontWeight: 600, color: 'var(--text-new)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.first_name} {m.last_name}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-color-calendar-week)', flexShrink: 0 }}>
                {m.workingStatus?.emoji} {m.workingStatus?.name}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {pendingReports.length === 0 && pendingExpenses.length === 0 && teamWithStatus.length === 0 && (
        <div style={{ padding: '32px 12px', textAlign: 'center', color: 'var(--text-color-calendar-week)' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>✨</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>All clear</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>Nothing needs your attention</div>
        </div>
      )}

    </div>
  );
};

export default SmartSummaryTab;
